/**
 * STL Uploader - Cloud Slicer Integration
 * Connects to MNM3D API for slicing and quote generation
 */

const STLUploader = (function() {
  const API_BASE = 'https://api2.mnm3d.com';
  const WHATSAPP_NUMBER = '919974440949';

  // Specialty material types
  const SPECIALTY_TYPES = ['PLA_GLOW', 'PLA_CF'];

  const SPECIALTY_INFO = {
    'PLA_GLOW': {
      badge: 'Specialty',
      note: 'Glows in the dark after exposure to light. Perfect for decorative items.',
      icon: 'glow'
    },
    'PLA_CF': {
      badge: 'Premium',
      note: 'Carbon fiber reinforced for exceptional strength. Ideal for structural parts.',
      icon: 'carbon'
    }
  };

  // Layer heights based on nozzle size
  const LAYER_HEIGHTS_BY_NOZZLE = {
    '0.2': [
      { value: 0.05, label: '0.05mm (Ultra Fine)' },
      { value: 0.08, label: '0.08mm (Extra Fine)' },
      { value: 0.1, label: '0.10mm (Fine)' },
      { value: 0.12, label: '0.12mm (Standard)' },
      { value: 0.15, label: '0.15mm (Fast)' },
    ],
    '0.4': [
      { value: 0.08, label: '0.08mm (Ultra Fine)' },
      { value: 0.12, label: '0.12mm (Fine)' },
      { value: 0.16, label: '0.16mm (Detail)' },
      { value: 0.2, label: '0.20mm (Standard)' },
      { value: 0.24, label: '0.24mm (Draft)' },
      { value: 0.28, label: '0.28mm (Fast)' },
    ],
    '0.6': [
      { value: 0.15, label: '0.15mm (Fine)' },
      { value: 0.2, label: '0.20mm (Detail)' },
      { value: 0.3, label: '0.30mm (Standard)' },
      { value: 0.36, label: '0.36mm (Draft)' },
      { value: 0.42, label: '0.42mm (Fast)' },
    ],
    '0.8': [
      { value: 0.2, label: '0.20mm (Fine)' },
      { value: 0.3, label: '0.30mm (Detail)' },
      { value: 0.4, label: '0.40mm (Standard)' },
      { value: 0.48, label: '0.48mm (Draft)' },
      { value: 0.56, label: '0.56mm (Fast)' },
    ],
  };

  const DEFAULT_LAYER_HEIGHT = {
    '0.2': 0.12,
    '0.4': 0.2,
    '0.6': 0.3,
    '0.8': 0.4,
  };

  let state = {
    file: null,
    fileArrayBuffer: null,
    settings: null,
    quote: null,
    isSlicing: false,
    selectedMaterial: null,
    selectedMaterialHex: null,
    selectedMaterialType: null,
    selectedNozzle: 0.4,
    selectedLayerHeight: 0.2,
    selectedInfill: 20,
    supportEnable: true,
    quantity: 1
  };

  const elements = {};

  async function init() {
    cacheElements();
    await fetchSettings();
    bindEvents();
    initAdvancedSettings();
    updateStep(1);
  }

  function cacheElements() {
    // Progress steps
    elements.steps = document.querySelectorAll('.stl-step');

    // Upload
    elements.dropzone = document.getElementById('stl-dropzone');
    elements.fileInput = document.getElementById('stl-file-input');
    elements.fileInfo = document.getElementById('stl-file-info');
    elements.fileName = document.getElementById('stl-file-name');
    elements.fileSize = document.getElementById('stl-file-size');

    // Preview
    elements.previewArea = document.getElementById('model-preview-area');
    elements.previewFilename = document.getElementById('preview-filename');
    elements.previewColorDot = document.getElementById('preview-color-dot');

    // Config
    elements.configPanel = document.getElementById('stl-config-panel');
    elements.materialGridContainer = document.getElementById('material-grid-container');
    elements.specialtyContainer = document.getElementById('specialty-materials-container');
    elements.specialtyGrid = document.getElementById('specialty-grid');
    elements.selectedMaterialInfo = document.getElementById('selected-material-info');

    // Advanced settings
    elements.advancedToggle = document.getElementById('advanced-toggle');
    elements.advancedPanel = document.getElementById('advanced-panel');
    elements.nozzleSelect = document.getElementById('nozzle-select');
    elements.layerHeightSelect = document.getElementById('layer-height-select');
    elements.layerHeightHint = document.getElementById('layer-height-hint');
    elements.infillSlider = document.getElementById('infill-slider');
    elements.infillValue = document.getElementById('infill-value');
    elements.infillHint = document.getElementById('infill-hint');
    elements.supportToggle = document.getElementById('support-toggle');
    elements.settingsSummary = document.getElementById('settings-summary-text');

    // Quantity
    elements.qtyMinus = document.getElementById('qty-minus');
    elements.qtyPlus = document.getElementById('qty-plus');
    elements.quantityDisplay = document.getElementById('quantity-display');
    elements.quantityInput = document.getElementById('quantity-input');

    // Buttons
    elements.sliceBtn = document.getElementById('slice-btn');
    elements.addToCartBtn = document.getElementById('add-to-cart-btn');
    elements.newQuoteBtn = document.getElementById('new-quote-btn');

    // Help buttons
    elements.helpPriceBtn = document.getElementById('help-price-btn');
    elements.helpQuestionsBtn = document.getElementById('help-questions-btn');
    elements.helpPricePanel = document.getElementById('help-price-panel');
    elements.helpQuestionsPanel = document.getElementById('help-questions-panel');
    elements.whatsappPriceLink = document.getElementById('whatsapp-price-link');
    elements.whatsappQuestionsLink = document.getElementById('whatsapp-questions-link');

    // Panels
    elements.quotePanel = document.getElementById('quote-panel');
    elements.quotePrice = document.getElementById('quote-price');
    elements.quotePrintTime = document.getElementById('quote-print-time');
    elements.quoteMaterial = document.getElementById('quote-material-usage');
    elements.loadingOverlay = document.getElementById('loading-overlay');
    elements.errorMessage = document.getElementById('error-message');
  }

  async function fetchSettings() {
    try {
      const response = await fetch(`${API_BASE}/settings`);
      if (!response.ok) throw new Error('Failed to fetch settings');
      const data = await response.json();
      state.settings = data.availableSettings || data;
      renderMaterialGrid();
    } catch (error) {
      showError('Unable to load print settings. Please refresh the page.');
      console.error('Settings fetch error:', error);
    }
  }

  function renderMaterialGrid() {
    if (!state.settings?.materials) return;

    const materials = state.settings.materials;
    const regular = {};
    const specialty = [];

    // Group materials
    materials.forEach(mat => {
      if (SPECIALTY_TYPES.includes(mat.type)) {
        specialty.push(mat);
      } else {
        if (!regular[mat.type]) regular[mat.type] = [];
        regular[mat.type].push(mat);
      }
    });

    // Render regular materials by type
    let regularHtml = '';
    Object.entries(regular).forEach(([type, mats]) => {
      regularHtml += `
        <div class="stl-material-type-section">
          <h4 class="stl-material-type-heading">${type}</h4>
          <div class="stl-color-grid">
            ${mats.map(mat => `
              <button type="button"
                      class="stl-color-swatch"
                      data-material-id="${mat.id}"
                      data-hex="${mat.hex}"
                      data-type="${mat.type}"
                      data-color="${mat.color}"
                      data-series="${mat.series || mat.brand}"
                      title="${mat.color} - ${mat.series || mat.brand}">
                <span class="stl-swatch-color" style="background-color: ${mat.hex};"></span>
                <span class="stl-swatch-label">${mat.color}</span>
              </button>
            `).join('')}
          </div>
        </div>
      `;
    });
    elements.materialGridContainer.innerHTML = regularHtml;

    // Render specialty materials
    if (specialty.length > 0) {
      let specialtyHtml = '';
      specialty.forEach(mat => {
        const info = SPECIALTY_INFO[mat.type] || {};
        const isGlow = mat.type === 'PLA_GLOW';
        specialtyHtml += `
          <button type="button"
                  class="stl-specialty-card"
                  data-material-id="${mat.id}"
                  data-hex="${mat.hex}"
                  data-type="${mat.type}"
                  data-color="${mat.color}"
                  data-series="${mat.series || mat.brand}">
            <div class="stl-specialty-swatch ${isGlow ? 'glow' : ''}"
                 style="background-color: ${mat.hex};">
              ${isGlow ?
                '<svg class="stl-specialty-badge" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>' :
                '<svg class="stl-specialty-badge" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>'
              }
            </div>
            <div class="stl-specialty-info">
              <span class="stl-specialty-name">
                ${mat.color}
                <span class="stl-specialty-tag">${info.badge || 'Specialty'}</span>
              </span>
              <span class="stl-specialty-desc">${info.note || mat.series || mat.brand}</span>
            </div>
          </button>
        `;
      });
      elements.specialtyGrid.innerHTML = specialtyHtml;
      elements.specialtyContainer.classList.remove('hidden');
    }

    // Bind click handlers
    elements.materialGridContainer.querySelectorAll('.stl-color-swatch').forEach(btn => {
      btn.addEventListener('click', handleMaterialSelect);
    });
    if (elements.specialtyGrid) {
      elements.specialtyGrid.querySelectorAll('.stl-specialty-card').forEach(btn => {
        btn.addEventListener('click', handleMaterialSelect);
      });
    }
  }

  function handleMaterialSelect(e) {
    const btn = e.currentTarget;
    const materialId = btn.dataset.materialId;
    const hex = btn.dataset.hex;
    const type = btn.dataset.type;
    const color = btn.dataset.color;
    const series = btn.dataset.series;

    // Update state
    state.selectedMaterial = materialId;
    state.selectedMaterialHex = hex;
    state.selectedMaterialType = type;

    // Update UI - remove previous selection
    document.querySelectorAll('.stl-color-swatch.selected, .stl-specialty-card.selected')
      .forEach(el => el.classList.remove('selected'));
    btn.classList.add('selected');

    // Show selected info
    if (elements.selectedMaterialInfo) {
      elements.selectedMaterialInfo.querySelector('.stl-info-swatch').style.backgroundColor = hex;
      elements.selectedMaterialInfo.querySelector('.stl-info-name').textContent = color;
      elements.selectedMaterialInfo.querySelector('.stl-info-type').textContent = `${type} - ${series}`;
      elements.selectedMaterialInfo.classList.remove('hidden');
    }

    // Update preview color dot
    if (elements.previewColorDot) {
      elements.previewColorDot.style.backgroundColor = hex;
      elements.previewColorDot.classList.remove('hidden');
    }

    // Enable slice button
    if (elements.sliceBtn) {
      elements.sliceBtn.disabled = false;
    }

    // Reset quote if material changed
    if (state.quote) {
      state.quote = null;
      elements.quotePanel?.classList.add('hidden');
    }
  }

  function initAdvancedSettings() {
    // Initialize layer heights for default nozzle
    updateLayerHeightOptions();
    updateSettingsSummary();
  }

  function updateLayerHeightOptions() {
    const nozzle = state.selectedNozzle.toString();
    const heights = LAYER_HEIGHTS_BY_NOZZLE[nozzle] || LAYER_HEIGHTS_BY_NOZZLE['0.4'];
    const defaultHeight = DEFAULT_LAYER_HEIGHT[nozzle];

    if (elements.layerHeightSelect) {
      elements.layerHeightSelect.innerHTML = heights.map(lh =>
        `<option value="${lh.value}" ${lh.value === defaultHeight ? 'selected' : ''}>
          ${lh.label}
        </option>`
      ).join('');
    }

    state.selectedLayerHeight = defaultHeight;

    if (elements.layerHeightHint) {
      elements.layerHeightHint.textContent = `Layer heights optimized for ${nozzle}mm nozzle`;
    }
  }

  function getInfillDescription(density) {
    if (density <= 15) return 'Low - Faster, less material';
    if (density <= 30) return 'Medium - Balanced strength and speed';
    if (density <= 50) return 'High - Strong parts';
    return 'Very high - Maximum strength';
  }

  function updateSettingsSummary() {
    if (elements.settingsSummary) {
      const support = state.supportEnable ? 'on' : 'off';
      elements.settingsSummary.innerHTML =
        `<strong>Default:</strong> ${state.selectedNozzle}mm nozzle, ${state.selectedLayerHeight}mm layer, ${state.selectedInfill}% infill, supports ${support}`;
    }
  }

  function bindEvents() {
    // Drag and drop
    if (elements.dropzone) {
      elements.dropzone.addEventListener('dragover', handleDragOver);
      elements.dropzone.addEventListener('dragleave', handleDragLeave);
      elements.dropzone.addEventListener('drop', handleDrop);
      elements.dropzone.addEventListener('click', () => elements.fileInput?.click());
    }

    // File input
    if (elements.fileInput) {
      elements.fileInput.addEventListener('change', handleFileSelect);
    }

    // Advanced settings toggle
    if (elements.advancedToggle) {
      elements.advancedToggle.addEventListener('click', toggleAdvancedPanel);
    }

    // Nozzle change
    if (elements.nozzleSelect) {
      elements.nozzleSelect.addEventListener('change', handleNozzleChange);
    }

    // Layer height change
    if (elements.layerHeightSelect) {
      elements.layerHeightSelect.addEventListener('change', () => {
        state.selectedLayerHeight = parseFloat(elements.layerHeightSelect.value);
        updateSettingsSummary();
      });
    }

    // Infill slider
    if (elements.infillSlider) {
      elements.infillSlider.addEventListener('input', handleInfillChange);
    }

    // Support toggle
    if (elements.supportToggle) {
      elements.supportToggle.addEventListener('change', () => {
        state.supportEnable = elements.supportToggle.checked;
        updateSettingsSummary();
      });
    }

    // Quantity buttons
    if (elements.qtyMinus) {
      elements.qtyMinus.addEventListener('click', () => handleQuantityChange(-1));
    }
    if (elements.qtyPlus) {
      elements.qtyPlus.addEventListener('click', () => handleQuantityChange(1));
    }

    // Slice button
    if (elements.sliceBtn) {
      elements.sliceBtn.addEventListener('click', handleSlice);
    }

    // Add to cart
    if (elements.addToCartBtn) {
      elements.addToCartBtn.addEventListener('click', handleAddToCart);
    }

    // New quote
    if (elements.newQuoteBtn) {
      elements.newQuoteBtn.addEventListener('click', resetForm);
    }

    // Help buttons
    if (elements.helpPriceBtn) {
      elements.helpPriceBtn.addEventListener('click', () => toggleHelpPanel('price'));
    }
    if (elements.helpQuestionsBtn) {
      elements.helpQuestionsBtn.addEventListener('click', () => toggleHelpPanel('questions'));
    }
  }

  function toggleHelpPanel(type) {
    const pricePanel = elements.helpPricePanel;
    const questionsPanel = elements.helpQuestionsPanel;
    const priceBtn = elements.helpPriceBtn;
    const questionsBtn = elements.helpQuestionsBtn;

    if (type === 'price') {
      const isOpen = !pricePanel.classList.contains('hidden');
      pricePanel.classList.toggle('hidden', isOpen);
      questionsPanel.classList.add('hidden');
      priceBtn.classList.toggle('active', !isOpen);
      questionsBtn.classList.remove('active');

      if (!isOpen) {
        updateWhatsAppLinks();
      }
    } else {
      const isOpen = !questionsPanel.classList.contains('hidden');
      questionsPanel.classList.toggle('hidden', isOpen);
      pricePanel.classList.add('hidden');
      questionsBtn.classList.toggle('active', !isOpen);
      priceBtn.classList.remove('active');

      if (!isOpen) {
        updateWhatsAppLinks();
      }
    }
  }

  function updateWhatsAppLinks() {
    const summary = state.quote?.summary || state.quote;
    const price = summary?.totalPrice || summary?.estimatedPrice || 0;
    const priceFormatted = `₹${Math.round(price).toLocaleString()}`;

    const priceMessage = encodeURIComponent(
      `Hi! I got a quote for ${priceFormatted} but it's a bit high for me. Can we discuss options?`
    );
    const questionsMessage = encodeURIComponent(
      `Hi! I have a question about my 3D print quote (${priceFormatted}).`
    );

    if (elements.whatsappPriceLink) {
      elements.whatsappPriceLink.href = `https://wa.me/${WHATSAPP_NUMBER}?text=${priceMessage}`;
    }
    if (elements.whatsappQuestionsLink) {
      elements.whatsappQuestionsLink.href = `https://wa.me/${WHATSAPP_NUMBER}?text=${questionsMessage}`;
    }
  }

  function toggleAdvancedPanel() {
    const isHidden = elements.advancedPanel.classList.contains('hidden');
    elements.advancedPanel.classList.toggle('hidden');
    elements.advancedToggle.classList.toggle('expanded', isHidden);
  }

  function handleNozzleChange() {
    state.selectedNozzle = parseFloat(elements.nozzleSelect.value);
    updateLayerHeightOptions();
    updateSettingsSummary();
  }

  function handleInfillChange() {
    const value = parseInt(elements.infillSlider.value);
    state.selectedInfill = value;
    if (elements.infillValue) {
      elements.infillValue.textContent = value;
    }
    if (elements.infillHint) {
      elements.infillHint.textContent = getInfillDescription(value);
    }
    updateSettingsSummary();
  }

  function handleQuantityChange(delta) {
    state.quantity = Math.max(1, state.quantity + delta);
    updateQuantityDisplay();
  }

  function updateQuantityDisplay() {
    if (elements.quantityDisplay) {
      elements.quantityDisplay.textContent = state.quantity;
    }
    if (elements.quantityInput) {
      elements.quantityInput.value = state.quantity;
    }
    if (elements.qtyMinus) {
      elements.qtyMinus.disabled = state.quantity <= 1;
    }
  }

  function updateStep(step) {
    elements.steps.forEach((el, index) => {
      const stepNum = index + 1;
      el.classList.remove('active', 'completed');
      if (stepNum === step) {
        el.classList.add('active');
      } else if (stepNum < step) {
        el.classList.add('completed');
      }
    });
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    elements.dropzone.classList.add('dragover');
  }

  function handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    elements.dropzone.classList.remove('dragover');
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    elements.dropzone.classList.remove('dragover');

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFile(files[0]);
    }
  }

  function handleFileSelect(e) {
    const files = e.target.files;
    if (files.length > 0) {
      processFile(files[0]);
    }
  }

  function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  function processFile(file) {
    // Validate file type
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.stl')) {
      showError('Please upload an STL file.');
      return;
    }

    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      showError('File size must be less than 50MB.');
      return;
    }

    state.file = file;
    hideError();

    // Read file as ArrayBuffer for future 3D preview
    const reader = new FileReader();
    reader.onload = (e) => {
      state.fileArrayBuffer = e.target.result;
      // Future: window.STLViewer?.loadFromArrayBuffer(state.fileArrayBuffer);
    };
    reader.readAsArrayBuffer(file);

    // Update file info
    if (elements.fileName) {
      elements.fileName.textContent = file.name;
    }
    if (elements.fileSize) {
      elements.fileSize.textContent = formatFileSize(file.size);
    }
    if (elements.fileInfo) {
      elements.fileInfo.classList.remove('hidden');
    }

    // Show preview area
    if (elements.previewArea) {
      elements.previewArea.classList.remove('hidden');
      if (elements.previewFilename) {
        elements.previewFilename.textContent = file.name;
      }
    }

    // Show config panel
    if (elements.configPanel) {
      elements.configPanel.classList.remove('hidden');
    }

    // Update progress
    updateStep(2);

    // Reset quote
    if (elements.quotePanel) {
      elements.quotePanel.classList.add('hidden');
    }
    state.quote = null;

    // Disable slice button until material selected
    if (elements.sliceBtn) {
      elements.sliceBtn.disabled = !state.selectedMaterial;
    }
  }

  async function handleSlice() {
    if (!state.file) {
      showError('Please upload a file first.');
      return;
    }

    if (!state.selectedMaterial) {
      showError('Please select a material and color.');
      return;
    }

    state.isSlicing = true;
    setButtonLoading(elements.sliceBtn, true);
    hideError();

    try {
      const formData = new FormData();
      formData.append('uploaded_file', state.file);
      formData.append('settings', JSON.stringify({
        material: state.selectedMaterial,
        layerHeight: state.selectedLayerHeight,
        infill: state.selectedInfill,
        nozzle: state.selectedNozzle,
        supportEnable: state.supportEnable,
        quantity: state.quantity
      }));

      // Use combined Shopify endpoint that slices + creates variant in one call
      const response = await fetch(`${API_BASE}/shopify/quote`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Slicing failed');
      }

      const result = await response.json();
      state.quote = result;
      displayQuote(result);
      updateStep(3);
    } catch (error) {
      showError(`Slicing failed: ${error.message}`);
      console.error('Slice error:', error);
    } finally {
      state.isSlicing = false;
      setButtonLoading(elements.sliceBtn, false);
    }
  }

  function displayQuote(quote) {
    if (!elements.quotePanel) return;

    const summary = quote.summary || quote;

    if (elements.quotePrice) {
      const price = summary.totalPrice || summary.estimatedPrice || 0;
      elements.quotePrice.textContent = `₹${price.toFixed(0)}`;
    }

    if (elements.quotePrintTime) {
      elements.quotePrintTime.textContent = summary.printTime || '--';
    }

    if (elements.quoteMaterial) {
      const grams = summary.materialUsedGrams || 0;
      elements.quoteMaterial.textContent = `${grams.toFixed(1)}g`;
    }

    elements.quotePanel.classList.remove('hidden');

    // Scroll to quote
    elements.quotePanel.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  async function handleAddToCart() {
    if (!state.quote || !state.file) {
      showError('Please get a quote first.');
      return;
    }

    // Variant is already created during quote - use it directly
    const variantId = state.quote.variantId;
    if (!variantId) {
      showError('Quote is missing variant information. Please get a new quote.');
      return;
    }

    const summary = state.quote.summary || state.quote;
    const material = state.settings?.materials?.find(m => m.id === state.selectedMaterial);
    const materialName = material ? `${material.type} - ${material.color}` : state.selectedMaterial;

    setButtonLoading(elements.addToCartBtn, true);

    try {
      // Add the pre-created variant to cart
      const cartResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: state.quantity,
          properties: {
            '_File Name': state.file.name,
            '_Material': materialName,
            '_Layer Height': `${state.selectedLayerHeight}mm`,
            '_Infill': `${state.selectedInfill}%`,
            '_Print Time': summary.printTime,
            '_Quote ID': state.quote.quoteId
          }
        })
      });

      if (!cartResponse.ok) {
        throw new Error('Failed to add to cart');
      }

      // Trigger cart update event
      document.dispatchEvent(new CustomEvent('cart:updated'));

      // Show success or redirect to cart
      if (window.STLUploaderConfig?.redirectToCart) {
        window.location.href = '/cart';
      } else {
        showToast('Added to cart!', 'success');
        resetForm();
      }
    } catch (error) {
      showError('Failed to add to cart. Please try again.');
      console.error('Add to cart error:', error);
    } finally {
      setButtonLoading(elements.addToCartBtn, false);
    }
  }

  function setButtonLoading(btn, loading) {
    if (!btn) return;
    if (loading) {
      btn.classList.add('loading');
      btn.disabled = true;
    } else {
      btn.classList.remove('loading');
      btn.disabled = false;
    }
  }

  function resetForm() {
    state.file = null;
    state.fileArrayBuffer = null;
    state.quote = null;
    state.selectedMaterial = null;
    state.selectedMaterialHex = null;
    state.selectedMaterialType = null;
    state.quantity = 1;

    if (elements.fileInput) elements.fileInput.value = '';
    if (elements.fileName) elements.fileName.textContent = '';
    if (elements.fileSize) elements.fileSize.textContent = '';
    if (elements.fileInfo) elements.fileInfo.classList.add('hidden');
    if (elements.previewArea) elements.previewArea.classList.add('hidden');
    if (elements.previewColorDot) elements.previewColorDot.classList.add('hidden');
    if (elements.configPanel) elements.configPanel.classList.add('hidden');
    if (elements.quotePanel) elements.quotePanel.classList.add('hidden');
    if (elements.selectedMaterialInfo) elements.selectedMaterialInfo.classList.add('hidden');
    if (elements.sliceBtn) elements.sliceBtn.disabled = true;

    // Reset material selection
    document.querySelectorAll('.stl-color-swatch.selected, .stl-specialty-card.selected')
      .forEach(el => el.classList.remove('selected'));

    // Reset quantity
    updateQuantityDisplay();

    // Reset progress
    updateStep(1);

    // Scroll to top
    elements.dropzone?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function showError(message) {
    if (elements.errorMessage) {
      elements.errorMessage.textContent = message;
      elements.errorMessage.classList.remove('hidden');
    }
    showToast(message, 'error', 5000);
  }

  function hideError() {
    if (elements.errorMessage) {
      elements.errorMessage.classList.add('hidden');
    }
  }

  function showToast(message, type = 'success', duration = 3000) {
    const toast = document.createElement('div');
    toast.className = `stl-toast ${type}`;

    const iconSvg = type === 'success'
      ? '<svg class="stl-toast-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>'
      : type === 'error'
      ? '<svg class="stl-toast-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>'
      : '';

    toast.innerHTML = `${iconSvg}<span>${message}</span>`;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.animation = 'stl-toast-out 0.3s ease forwards';
      setTimeout(() => toast.remove(), 300);
    }, duration);
  }

  // Expose hooks for external 3D viewer integration
  window.STLUploaderHooks = {
    onFileLoaded: (callback) => {
      state._onFileLoadedCallback = callback;
    },
    getFileData: () => ({
      file: state.file,
      arrayBuffer: state.fileArrayBuffer,
    }),
    setDimensions: (dims) => {
      state.fileDimensions = dims;
    },
    getState: () => ({ ...state }),
  };

  return { init };
})();

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', STLUploader.init);
} else {
  STLUploader.init();
}
